import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEightD } from '../contexts/EightDContext';
import { Plus, FileText, Clock, CheckCircle, Search } from 'lucide-react';
import { EightDStatus } from '../types/eightd.types';

const EightDList: React.FC = () => {
    const navigate = useNavigate();
    const { reports, loading, getMetrics } = useEightD();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<EightDStatus | 'All'>('All');

    const metrics = getMetrics();

    const filtered = reports.filter(report => {
        const matchesSearch =
            report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.eightDNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.customerName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || report.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const statusColors: Record<EightDStatus, string> = {
        'Draft': 'bg-gray-100 text-gray-700',
        'In Progress': 'bg-blue-100 text-blue-700',
        'Review': 'bg-yellow-100 text-yellow-700',
        'Approved': 'bg-purple-100 text-purple-700',
        'Implemented': 'bg-green-100 text-green-700',
        'Closed': 'bg-gray-200 text-gray-600',
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-600">Loading 8D reports...</div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">8D Problem Solving</h1>
                    <p className="text-gray-600 mt-1">8 Disciplines structured problem-solving methodology</p>
                </div>

                <Link
                    to="/8d/create"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    New 8D Report
                </Link>
            </div>

            {/* Metrics Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Total Reports</span>
                        <FileText className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{metrics.total}</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Active</span>
                        <Clock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="text-2xl font-bold text-blue-600">{metrics.active}</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Completed</span>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="text-2xl font-bold text-green-600">{metrics.completed}</div>
                    <div className="text-xs text-gray-500 mt-1">{metrics.completionRate}% completion rate</div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Avg Days</span>
                        <Clock className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="text-2xl font-bold text-orange-600">{metrics.averageTimeToComplete}</div>
                    <div className="text-xs text-gray-500 mt-1">to complete</div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by 8D#, title, product, or customer..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as EightDStatus | 'All')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Draft">Draft</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Review">Review</option>
                            <option value="Approved">Approved</option>
                            <option value="Implemented">Implemented</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Reports Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {filtered.length > 0 ? (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">8D Number</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filtered.map((report) => (
                                <tr
                                    key={report.id}
                                    onClick={() => navigate(`/8d/${report.id}`)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <td className="px-4 py-3">
                                        <span className="font-mono text-sm font-medium text-blue-600">
                                            {report.eightDNumber}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">{report.title}</div>
                                        {report.fracasNumber && (
                                            <div className="text-xs text-gray-500">
                                                FRACAS: {report.fracasNumber}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{report.productName}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{report.customerName}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[report.status]}`}>
                                            {report.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {report.createdAt.toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-12 text-center">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">
                            {searchTerm || statusFilter !== 'All'
                                ? 'No 8D reports match your search criteria'
                                : 'No 8D reports yet'}
                        </p>
                        <Link
                            to="/8d/create"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create First 8D Report
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EightDList;
